import { Text } from '@daily/shared/components/Text';
import {
  ExtendedDailyParticipant,
  useLocalParticipant,
  useParticipantIds,
} from '@daily-co/daily-react-hooks';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtual } from 'react-virtual';

import { useCallState } from '../../contexts/CallProvider';
import { useIsMobile } from '../../contexts/UIState';
import { useCallConfig } from '../../hooks/useCallConfig';
import { useStyleVariants } from '../../hooks/useStyleVariants';
import { ParticipantRow } from './ParticipantRow';
import { PeopleActions } from './PeopleActions';
import { WaitingList } from './WaitingList';

/**
 * Sort participants alphabetically by user_name. Put the local user first.
 */
const sortParticipantIds = (
  a: ExtendedDailyParticipant,
  b: ExtendedDailyParticipant
) => {
  if (a.local || b.local) return 1;
  if (a.user_name.toLowerCase() < b.user_name.toLowerCase()) return -1;
  if (a.user_name.toLowerCase() > b.user_name.toLowerCase()) return 1;
  return 0;
};

export const People: React.FC = () => {
  const { t } = useTranslation();
  const [isMobile] = useIsMobile();
  const { iconVariant, textVariant, textVariantSmall } = useStyleVariants();
  const { broadcastRole } = useCallConfig();
  const { disableAudio } = useCallState();
  const localParticipant = useLocalParticipant();

  const peopleRef = useRef<HTMLDivElement>(null);

  const [localId, ...otherIds] = useParticipantIds({
    sort: sortParticipantIds,
  });

  const stackGap = isMobile ? 16 : 8;

  const itemHeight = useMemo(() => {
    const fontSize = textVariant === 'base' ? 16 : 24;
    return fontSize + stackGap;
  }, [stackGap, textVariant]);

  const virtualizer = useVirtual({
    size: otherIds.length,
    parentRef: peopleRef,
    estimateSize: useCallback(() => itemHeight, [itemHeight]),
    overscan: 5,
  });

  return (
    <div ref={peopleRef} className="people">
      <WaitingList />
      <div className="list">
        <Text color="muted" variant={textVariantSmall}>
          {t('people.withCount', { count: otherIds.length + 1 })}
        </Text>
        <ParticipantRow
          iconVariant={iconVariant}
          localParticipant={localParticipant}
          showActions
          sessionId={localId}
          showMicMutedState={!disableAudio}
          textVariant={textVariant}
        />
        <div className="rows" style={{ height: `${virtualizer.totalSize}px` }}>
          {virtualizer.virtualItems.map((row) => (
            <div
              key={row.index}
              className="row"
              style={{
                transform: `translateY(${row.start}px)`,
              }}
            >
              <ParticipantRow
                iconVariant={iconVariant}
                localParticipant={localParticipant}
                showActions={broadcastRole !== 'attendee'}
                sessionId={otherIds[row.index]}
                showMicMutedState={!disableAudio}
                textVariant={textVariant}
              />
            </div>
          ))}
        </div>
      </div>
      <PeopleActions />
      <style jsx>{`
        .people {
          display: flex;
          flex-direction: column;
          gap: 24px;
          height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          padding: var(--card-spacing);
          position: relative;
        }
        .list {
          display: flex;
          flex-direction: column;
          position: relative;
        }
        .list :global(> :not(:first-child)) {
          padding-top: ${stackGap}px;
        }
        .list .rows {
          padding: 0;
          position: relative;
        }
        .list .row {
          padding-top: ${stackGap}px;
          position: absolute;
          width: 100%;
        }
      `}</style>
    </div>
  );
};
